import { cloneDeep } from 'lodash';

import { Activity } from 'src/models/activity';
import { MutableAggregateId } from 'src/models/aggregate';
import { ModuleNameEnum } from 'src/models/module/enums';
import { StatusButton } from 'src/models/status';
import { StatusEnum } from 'src/models/status/enums';

import { encodeBase64 } from 'src/util/common';

const AUTHENTICATED_USER_INFORMATION_GETTER = 'userModule/AUTHENTICATED_USER_INFORMATION';

type AggregateUserContext =
	| {
			authenticatedUserInformation?: {
				id?: null | number | string;
			} | null;
	  }
	| {
			getters?: Record<string, unknown>;
	  }
	| {
			id?: null | number | string;
	  }
	| null
	| undefined;

function resolveUserId(context: AggregateUserContext): number {
	if (context == null) {
		return 0;
	}

	if ('id' in context && typeof context.id === 'number') {
		return context.id;
	}

	if ('authenticatedUserInformation' in context) {
		const user = context.authenticatedUserInformation;
		if (user != null && typeof user.id === 'number') {
			return user.id;
		}
	}

	if ('getters' in context && context.getters != null) {
		const authenticatedUserInformation = context.getters[AUTHENTICATED_USER_INFORMATION_GETTER] as
			| { id?: null | number | string }
			| undefined;
		if (authenticatedUserInformation != null && typeof authenticatedUserInformation.id === 'number') {
			return authenticatedUserInformation.id;
		}
	}

	return 0;
}

export const theCollectionHasAtLeastOneAggregateForTheModule = (
	aggregateIds: string[],
	desiredModuleName: ModuleNameEnum
): boolean => {
	const aggregateIdsFound = aggregateIds.filter((aggregateId) => {
		const parsedAggregateId = JSON.parse(aggregateId) as {
			moduleName: string;
		};
		return parsedAggregateId.moduleName === desiredModuleName;
	});

	return aggregateIdsFound.length > 0;
};

function parseMutableAggregateId(aggregateId: string): MutableAggregateId | null {
	if (aggregateId === '' || aggregateId === undefined) {
		return null;
	}

	try {
		const parsedAggregateId = JSON.parse(aggregateId) as unknown;

		if (parsedAggregateId !== null && typeof parsedAggregateId === 'object') {
			return parsedAggregateId as MutableAggregateId;
		}
	} catch (error) {
		console.error(error);
	}

	return null;
}

export function aggregateModuleName(aggregateId: string): string {
	const parsedAggregateId = parseMutableAggregateId(aggregateId);

	return typeof parsedAggregateId?.moduleName === 'string' ? parsedAggregateId.moduleName : '';
}

export function buildAggregateId(parts: { entryId: number; moduleName: string; userId: number }): string {
	const aggregateId: MutableAggregateId = {};

	aggregateId.moduleName = parts.moduleName;
	aggregateId.entryId = parts.entryId;
	aggregateId.userId = parts.userId;
	aggregateId.sourceContainerId = 0;
	aggregateId.targetContainerId = 0;

	return JSON.stringify(aggregateId);
}

export function aggregateEntryId(aggregateId: string): null | number {
	const parsedAggregateId = parseMutableAggregateId(aggregateId);

	return typeof parsedAggregateId?.entryId === 'number' ? parsedAggregateId.entryId : null;
}

export function aggregateUserId(aggregateId: string): null | number {
	const parsedAggregateId = parseMutableAggregateId(aggregateId);

	return typeof parsedAggregateId?.userId === 'number' ? parsedAggregateId.userId : null;
}

export function aggregateIdWithModuleNameAndUnsetSourceAndTargetContainerId(
	aggregateId: string,
	moduleName: string
): string {
	const parsedAggregateId = parseMutableAggregateId(aggregateId);

	if (parsedAggregateId === null) {
		return aggregateId;
	}

	parsedAggregateId.moduleName = moduleName;
	parsedAggregateId.sourceContainerId = 0;
	parsedAggregateId.targetContainerId = 0;

	return JSON.stringify(parsedAggregateId);
}

export function setTheStateForTheAggregateIds(
	collection: Array<Activity>,
	aggregateIds: string[],
	state: StatusEnum
): Array<Activity> {
	let collectionChanged = false;
	const collectionCopy = cloneDeep<Array<Activity>>(collection);
	aggregateIds.forEach((aggregateId) => {
		const aggregateIndex = collectionCopy.findIndex((activity: Activity) => {
			return activity.aggregateId === aggregateId;
		});
		const aggregateAtIndex = collectionCopy[aggregateIndex];
		if (aggregateAtIndex !== undefined) {
			aggregateAtIndex.statusButton.status = state;
			collectionCopy[aggregateIndex] = aggregateAtIndex;
			collectionChanged = true;
		}
	});

	return collectionChanged ? collectionCopy : [];
}

export interface ApplyTheStatusForTheAggregateIdsPayload {
	aggregateIds: string[];
	status: StatusEnum;
}

export function applyTheStatusForTheAggregateIdsInRecordMaps<T extends { statusButton?: StatusButton }>(
	recordMaps: Array<Map<string, T>>,
	aggregateIds: string[],
	status: StatusEnum
): void {
	recordMaps.forEach((recordMap) => {
		aggregateIds.forEach((aggregateId) => {
			const normalizedAggregateId = aggregateIdWithUnsetSourceAndTargetContainerId(aggregateId);
			const record = recordMap.get(normalizedAggregateId);

			if (record?.statusButton === undefined || record.statusButton.status === status) {
				return;
			}

			recordMap.set(normalizedAggregateId, {
				...record,
				statusButton: { ...record.statusButton, status: status },
			});
		});
	});
}

export function removeTheAggregateIdsFromTheCollection(
	collection: Array<Activity>,
	aggregateIds: string[]
): Array<Activity> {
	let collectionCopy = cloneDeep<Array<Activity>>(collection);
	collectionCopy = collectionCopy.filter((aggregate: Activity) => {
		return !aggregateIds.includes(aggregate.aggregateId);
	});

	return collectionCopy.length != collection.length ? collectionCopy : collection;
}

export function removeScheduledTasksFromCollectionWithLogic(
	collection: Array<Activity>,
	aggregateIds: string[],
	targetDate: string,
	listType: 'inbox' | 'other' | 'taskList'
): Array<Activity> {
	if (listType === 'taskList') {
		return collection;
	}

	if (listType === 'inbox') {
		const today = new Date().toISOString().split('T')[0];
		if (targetDate > today) {
			return removeTheAggregateIdsFromTheCollection(collection, aggregateIds);
		}
		return collection;
	}

	return removeTheAggregateIdsFromTheCollection(collection, aggregateIds);
}

export function aggregateIdWithUnsetSourceAndTargetContainerId(aggregateId: string): string {
	const parsedAggregateId = parseMutableAggregateId(aggregateId);

	if (parsedAggregateId === null) {
		return aggregateId;
	}

	parsedAggregateId.sourceContainerId = 0;
	parsedAggregateId.targetContainerId = 0;

	return JSON.stringify(parsedAggregateId);
}

export function aggregateIdWithRemovedIdoAndInstanceId(aggregateId: string): string {
	const parsedAggregateId = parseMutableAggregateId(aggregateId);

	if (parsedAggregateId === null) {
		return aggregateId;
	}

	delete parsedAggregateId.idoId;
	delete parsedAggregateId.instanceId;

	return JSON.stringify(parsedAggregateId);
}

export function aggregateIdWithUnsetUserId(aggregateId: string, context: AggregateUserContext): string {
	const parsedAggregateId = parseMutableAggregateId(aggregateId);

	if (parsedAggregateId === null) {
		return aggregateId;
	}

	parsedAggregateId.userId = resolveUserId(context);

	return JSON.stringify(parsedAggregateId);
}

export function aggregateIdWithRemovedAggregatePayload(aggregateId: string): string {
	const parsedAggregateId = parseMutableAggregateId(aggregateId);

	if (parsedAggregateId === null) {
		return aggregateId;
	}

	delete parsedAggregateId.aggregatePayload;

	return JSON.stringify(parsedAggregateId);
}

export function aggregateIdWithExplicitSetUserId(aggregateId: string, userId: number | string): string {
	const parsedAggregateId = parseMutableAggregateId(aggregateId);

	if (parsedAggregateId === null) {
		return aggregateId;
	}

	const parsedUserId = typeof userId === 'number' ? userId : Number.parseInt(userId, 10);

	if (Number.isNaN(parsedUserId)) {
		return aggregateId;
	}

	parsedAggregateId.userId = parsedUserId;

	return JSON.stringify(parsedAggregateId);
}

export function routeForTheAggregate(aggregateId: string, tab?: 'history' | 'info'): string {
	const moduleName = aggregateModuleName(aggregateId);

	if (moduleName === '') {
		return '';
	}

	aggregateId = aggregateIdWithUnsetUserId(aggregateId, null);

	switch (moduleName) {
		case 'module_ido':
		case 'module_idoListResult':
			return `/record/${encodeBase64(aggregateId)}/module/view`;
		case 'module_document':
			return `/document/${encodeBase64(aggregateId)}/module/view`;
		case 'module_email':
			return `/mail/${encodeBase64(aggregateId)}/module/view`;
		case 'module_message':
			return `/message/${encodeBase64(aggregateId)}/module/view`;
		case 'module_note':
			return `/note/${encodeBase64(aggregateId)}/module/view`;
		case 'module_task':
			return `/task/${encodeBase64(aggregateId)}/module/view`;
		case 'module_activity': {
			const memoRoute = `/memo/${encodeBase64(aggregateId)}/module/view`;
			return tab === 'history' || tab === 'info' ? `${memoRoute}/${tab}` : memoRoute;
		}
		case 'module_appointment':
			return `/appointment/${encodeBase64(aggregateId)}/module/view`;
		case 'module_phonecall':
			return `/phoneCall/${encodeBase64(aggregateId)}/module/view/false`;
	}

	return '';
}
